import _ from 'lodash';
import { useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { FacilitiesTableRow } from './facilitiesTableOptions';
import {
  GetFacilitiesTableDataQueryGQLType,
  useGetFacilitiesTableDataQuery,
} from './getFacilitiesTableData.gql.generated';
import {
  CreditFacility,
  useCreditFacilities,
} from '~/contexts/CreditFacilitiesProvider/useCreditFacilities';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { getDefaultDashboardDisplayStartDate } from '~/packages/common-frontend/constants';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { parseInterestRateDisplayForCapitalProvider } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/utils';

// Helpers
const doesCovenantCalculationPass = ({
  limitType,
  limitValue,
  value,
}: GetFacilitiesTableDataQueryGQLType['latestCovenantCalculations'][number]): boolean => {
  if (limitType === 'max' && value < limitValue) return true;
  if (limitType === 'min' && value > limitValue) return true;
  return false;
};

const computeTotalsRow = (creditFacilityRows: FacilitiesTableRow[]) => {
  const totalsRow = creditFacilityRows.reduce(
    (acc, row) => ({
      ...acc,
      commitmentAmount: acc.commitmentAmount + row.commitmentAmount,
      loanOutstanding: acc.loanOutstanding + row.loanOutstanding,
      unusedCommitment: acc.unusedCommitment + row.unusedCommitment,
      collectionsAccountBalance:
        acc.collectionsAccountBalance + row.collectionsAccountBalance,
      requiredReserve: acc.requiredReserve + row.requiredReserve,
      totalBorrowingBase: acc.totalBorrowingBase + row.totalBorrowingBase,
      borrowingBaseFromReceivables:
        acc.borrowingBaseFromReceivables + row.borrowingBaseFromReceivables,
      borrowingBaseCashContribution:
        acc.borrowingBaseCashContribution + row.borrowingBaseCashContribution,
      accruedInterestAndFees:
        acc.accruedInterestAndFees + row.accruedInterestAndFees,
    }),
    {
      creditFacilityName: 'Portfolio',
      borrowerEntityName: '-',
      commitmentAmount: 0,
      loanOutstanding: 0,
      unusedCommitment: 0,
      utilization: 0,
      collectionsAccountBalance: 0,
      requiredReserve: 0,
      totalBorrowingBase: 0,
      borrowingBaseFromReceivables: 0,
      borrowingBaseCashContribution: 0,
      accruedInterestAndFees: 0,
      interestRates: '-',
      dateOfLastFundingRequest: undefined,
      financialCovenantsFailed: null,
      portfolioCovenantsFailed: null,
    },
  );

  // Utilization based on totals
  totalsRow.utilization =
    totalsRow.commitmentAmount > 0
      ? totalsRow.loanOutstanding / totalsRow.commitmentAmount
      : 0;

  return totalsRow;
};

const mapQueryDataToTableRows = ({
  creditFacilities,
  facilitiesTableQueryData: {
    organization: { currentCreditAgreementArtifacts },
    fundingRequests,
    latestLedgersForCreditFacilities,
    latestCovenantCalculations,
    latestBorrowingBaseReports,
  },
}: {
  creditFacilities: CreditFacility[];
  facilitiesTableQueryData: GetFacilitiesTableDataQueryGQLType;
}): FacilitiesTableRow[] => {
  const creditAgreements = currentCreditAgreementArtifacts.map(
    (creditAgreementFullArtifact) => {
      const parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementFullArtifact.creditAgreement,
      );
      if (!parsedCreditAgreement) {
        throw Error(
          `Expect creditAgreementFullArtifact to exist for creditAgreementId ${creditAgreementFullArtifact.id}`,
        );
      }
      return parsedCreditAgreement;
    },
  );

  // Bucket data from all of the different queries by credit facility
  const creditFacilitiesWithData = creditFacilities.map((creditFacility) => {
    const lastCreditAgreement = creditAgreements.find(
      (obj) => obj.creditFacilityId === creditFacility.id,
    );
    if (!lastCreditAgreement) {
      throw Error('Missing "lastCreditAgreement" in facilities table');
    }

    return {
      creditFacility,
      lastCreditAgreement,
      lastFundingRequest: _.orderBy(
        fundingRequests.nodes.filter(
          (obj) => obj.creditFacilityId === creditFacility.id,
        ),
        'created_at',
        'desc',
      )[0] as
        | GetFacilitiesTableDataQueryGQLType['fundingRequests']['nodes'][number]
        | undefined,
      ledgerBalanceForCreditFacility: latestLedgersForCreditFacilities.find(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
      covenantCalculationsForFacility: latestCovenantCalculations.filter(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
      borrowingBaseReport: latestBorrowingBaseReports.find(
        (obj) => obj?.creditFacilityId === creditFacility.id,
      ),
    };
  });

  // Map credit facility data to table row derived values
  const creditFacilityRows = creditFacilitiesWithData.map(
    ({
      creditFacility,
      lastCreditAgreement,
      lastFundingRequest,
      ledgerBalanceForCreditFacility,
      covenantCalculationsForFacility,
      borrowingBaseReport,
    }): FacilitiesTableRow => {
      return {
        // "Facility"
        creditFacilityName: creditFacility.formattedSlug,
        // "Borrower entity name"
        borrowerEntityName: creditFacility.borrowerEntityName,
        // "Global commitment amount"
        commitmentAmount: borrowingBaseReport?.commitmentAmount ?? 0,
        // "Outstanding Balance"
        loanOutstanding: borrowingBaseReport?.currentPrincipalBalance ?? 0,
        // "Unused Commitment"
        unusedCommitment:
          (borrowingBaseReport?.commitmentAmount ?? 0) -
          (borrowingBaseReport?.currentPrincipalBalance ?? 0),
        // "Utilitization"
        utilization: borrowingBaseReport?.totalFacilityUtilization ?? 0,
        // "Collections Account Balance"
        collectionsAccountBalance:
          borrowingBaseReport?.totalCollectionsAccountCash ?? 0,
        // "Required reserve"
        requiredReserve: borrowingBaseReport?.borrowingBaseReserveAmount ?? 0,
        // "Total Borrowing Base"
        // !TODO @danitian Remove derivation during borrowing base migration phase 3
        totalBorrowingBase:
          (borrowingBaseReport?.totalCashBorrowingBaseAmount ?? 0) +
          (borrowingBaseReport?.maxReceivableAdvanceAmount ?? 0),
        // "Borrowing Base from Receivables"
        borrowingBaseFromReceivables:
          borrowingBaseReport?.maxReceivableAdvanceAmount ?? 0,
        // "Borrowing Base from Cash"
        borrowingBaseCashContribution:
          borrowingBaseReport?.totalCashBorrowingBaseAmount ?? 0,
        // "Accrued Interest and Fees"
        accruedInterestAndFees:
          ledgerBalanceForCreditFacility?.ledger.balance ?? 0,
        // "Interest Rates"
        interestRates: lastCreditAgreement.facilityMechanics.parties.capitalProviders
          .map((capitalProvider) => {
            const rate = parseInterestRateDisplayForCapitalProvider(
              capitalProvider,
            );
            return `${capitalProvider.entity.name}: ${rate}`;
          })
          .join(', '),
        // "Date of Last Funding Request"
        dateOfLastFundingRequest: lastFundingRequest
          ? commonDate.epochMsToFullDateStr(lastFundingRequest.created_at)
          : undefined,
        // "Financial Covenants"
        financialCovenantsFailed: covenantCalculationsForFacility.reduce(
          (acc, covenantCalc) => {
            if (covenantCalc.covenantType === 'financial') {
              let nextTotal = 0;
              if (!doesCovenantCalculationPass(covenantCalc)) nextTotal += 1;
              if (typeof acc === 'number') nextTotal += acc;
              return nextTotal;
            }
            return acc;
          },
          null as number | null,
        ),
        // "Portfolio Covenants", this is always "null" for now
        // !TODO @danitian Ticket to compute this https://linear.app/finleycms/issue/ENG-9469/add-portcov-passfail-results-to-eldars-new-homepage-facility-summary
        portfolioCovenantsFailed: null,
      };
    },
  );
  return creditFacilityRows;
};

// Exports
export const useFacilitiesTableData = () => {
  const flags = useFeatureFlags();
  const { user } = useUserAuthOrThrow();
  const organizationId = user.organizationId;

  // Get credit facilities for this org
  const creditFacilitiesQuery = useCreditFacilities();
  const creditFacilities = useMemo(
    () => creditFacilitiesQuery.data?.organization.creditFacilities || [],
    [creditFacilitiesQuery.data?.organization.creditFacilities],
  );
  const creditFacilityIds = creditFacilities.map(({ id }) => id);

  // Get the credit facility details that we can query in aggregate
  const facilitiesTableQuery = useGetFacilitiesTableDataQuery({
    // No credit facility IDs, most likely waiting on the credit facility query
    skip: creditFacilityIds.length === 0,
    fetchPolicy: 'network-only',
    variables: {
      creditFacilityIds,
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
      ledgerAccountGroup: 'expensesFees',
      organizationId,
      startDate: commonDate.fullDateStrToEpoch(
        getDefaultDashboardDisplayStartDate(),
      ),
    },
  });

  const { computedRows, totalsRow } = useMemo(() => {
    const computed = facilitiesTableQuery.data
      ? mapQueryDataToTableRows({
          facilitiesTableQueryData: facilitiesTableQuery.data,
          creditFacilities,
        })
      : [];

    return {
      computedRows: computed,
      totalsRow: computeTotalsRow(computed),
    };
  }, [creditFacilities, facilitiesTableQuery.data]);

  return {
    computedRows,
    creditFacilities,
    totalsRow,
    error: facilitiesTableQuery.error || creditFacilitiesQuery.error,
    loading:
      creditFacilitiesQuery.loading ||
      !creditFacilitiesQuery.data ||
      facilitiesTableQuery.loading ||
      !facilitiesTableQuery.data,
  };
};
